import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Tooltip, Upload, message } from 'antd';
import {
  ArrowUpOutlined,
  PaperClipOutlined,
  CameraOutlined,
  CloseOutlined,
  FileImageOutlined,
  FileOutlined,
} from '@ant-design/icons';

// 实心圆角方块"停止"图标：与主流 LLM 输入框（Claude / ChatGPT）一致。
const StopIcon = (props) => (
  <svg
    viewBox="0 0 16 16"
    width="1em"
    height="1em"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <rect x="3" y="3" width="10" height="10" rx="1.5" />
  </svg>
);
import scss from './AiChatBotInputBox.scss';
import { ze } from 'utils/zhEn';

const { TextArea } = Input;

/**
 * AiDrawer 通用输入框。
 *
 * 关键能力：
 *   - P0-2 流式输出时（isStreaming=true）右下角"发送"按钮切换为"停止"按钮；
 *   - P0-3 中文输入法选词时按回车不发送（onCompositionStart/End + isComposing + keyCode 229）。
 */
const AiChatBotInputBox = (props) => {
  const {
    disableSend,
    onSend,
    onStop,
    isStreaming = false,
    leftElement = null,
    initialValue = '',
    onAttach,
    // 整体禁用（如工作区离线）：输入框 + 附件按钮 + 发送按钮都不再可交互
    disabled = false,
    // 覆盖默认 placeholder（如工作区离线时的「工作区离线，无法发送」）；不传保持原默认文案
    placeholder,
  } = props;
  const [value, setValue] = useState(initialValue || '');
  const [attachments, setAttachments] = useState([]);
  const textAreaRef = useRef(null);
  const isComposingRef = useRef(false);

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };
  const handleCompositionEnd = () => {
    isComposingRef.current = false;
  };

  /**
   * 统一发送逻辑：文本和附件一起发出（对齐 ChatGPT / Claude 交互）。
   * 有附件时走 onAttach 回调（将文件 base64 + 文本一起发），无附件时走 onSend。
   */
  const doSend = () => {
    const hasText = value.trim();
    const hasAttach = attachments.length > 0;
    if (disableSend || isStreaming || disabled) return;
    if (!hasText && !hasAttach) return;
    if (onAttach && hasAttach) {
      onAttach(attachments.map(a => a.file), value);
      setAttachments([]);
    } else {
      onSend(value);
    }
    setValue('');
  };

  const handleKeyDown = (e) => {
    if (isComposingRef.current || e.nativeEvent.isComposing || e.keyCode === 229) {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doSend();
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      const { textArea } = textAreaRef.current.resizableTextArea;
      const cursorPosition = textArea.selectionStart;
      const newValue = `${value.substring(0, cursorPosition)}\n${value.substring(cursorPosition)}`;
      setValue(newValue);
      setTimeout(() => {
        textArea.selectionStart = cursorPosition + 1;
        textArea.selectionEnd = cursorPosition + 1;
      }, 0);
    }
  };

  const handleChange = (e) => setValue(e.target.value);

  const handleBeforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    const maxSize = isImage ? 10 * 1024 * 1024 : 50 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error(ze(
        `文件不能超过 ${isImage ? '10' : '50'}MB`,
        `File cannot exceed ${isImage ? '10' : '50'}MB`
      ));
      return Upload.LIST_IGNORE;
    }
    return false; // 阻止自动上传，改为受控
  };

  const handleFileSelect = (file) => {
    setAttachments(prev => [...prev, {
      uid: file.uid || `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: file.name,
      file,
    }]);
    return false;
  };

  const removeAttachment = (uid) => {
    setAttachments(prev => {
      const found = prev.find(a => a.uid === uid);
      if (found?.thumbUrl) URL.revokeObjectURL(found.thumbUrl);
      return prev.filter(a => a.uid !== uid);
    });
  };

  // 组件卸载时清理所有 Object URL
  useEffect(() => {
    return () => {
      attachments.forEach(a => {
        if (a.thumbUrl) URL.revokeObjectURL(a.thumbUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = () => {
    doSend();
  };

  /**
   * Ctrl+V / Cmd+V 粘贴图片：当剪贴板含 image/* 类型时自动作为附件添加。
   */
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    let hasImage = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        hasImage = true;
        const file = item.getAsFile();
        if (!file) continue;
        // 生成更友好的文件名
        if (!file.name || file.name === 'image.png') {
          const ext = file.type.split('/')[1] || 'png';
          const renamed = new File([file], `pasted-${Date.now()}.${ext}`, { type: file.type });
          handleFileSelect(renamed);
        } else {
          handleFileSelect(file);
        }
      }
    }
    if (hasImage) {
      e.preventDefault();
    }
  };

  /**
   * 为图片附件生成缩略图 URL（仅图片类型）。
   */
  const getThumbUrl = (att) => {
    if (!att.file?.type?.startsWith('image/')) return null;
    if (att.thumbUrl) return att.thumbUrl;
    const url = URL.createObjectURL(att.file);
    att.thumbUrl = url; // 缓存到对象上避免重复创建
    return url;
  };

  /**
   * 格式化文件大小展示。
   */
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  /**
   * 根据文件类型返回对应图标。
   */
  const getFileIcon = (att) => {
    if (att.file?.type?.startsWith('image/')) return <FileImageOutlined />;
    return <FileOutlined />;
  };

  const handleStop = () => {
    if (onStop) onStop();
  };

  const sendOrStopBtn = isStreaming && onStop ? (
    <Tooltip title={ze('停止', 'Stop')} placement="top">
      <Button
        className={scss.stopButton}
        shape="circle"
        icon={<StopIcon />}
        onClick={handleStop}
        aria-label={ze('停止', 'Stop')}
      />
    </Tooltip>
  ) : (
    <Tooltip title={ze('发送', 'Send')} placement="top">
      <Button
        disabled={disableSend}
        className={scss.sendButton}
        shape="circle"
        icon={<ArrowUpOutlined />}
        onClick={handleSend}
        aria-label={ze('发送', 'Send')}
      />
    </Tooltip>
  );

  const hasLeftCluster = Boolean(leftElement);
  const hasAttachments = attachments.length > 0;

  return (
    <div className={scss.chatInputContainer}>
      {hasAttachments && (
        <div className={scss.attachPreviewBar}>
          {attachments.map(a => {
            const thumbUrl = getThumbUrl(a);
            const isImage = a.file?.type?.startsWith('image/');
            return (
              <div key={a.uid} className={scss.attachCard}>
                <div className={scss.attachCardThumb}>
                  {isImage && thumbUrl ? (
                    <img src={thumbUrl} alt={a.name} />
                  ) : (
                    <span className={scss.attachCardIcon}>{getFileIcon(a)}</span>
                  )}
                </div>
                <span className={scss.attachCardName} title={a.name}>{a.name}</span>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => removeAttachment(a.uid)}
                  className={scss.attachCardRemove}
                />
              </div>
            );
          })}
        </div>
      )}
      <div className={scss.chatInputInner}>
        <div className={scss.chatInputWrapper}>
          <div className={scss.textareaContainer}>
            <TextArea
              ref={textAreaRef}
              className={scss.chatInput}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              autoSize={{ minRows: 1, maxRows: 5 }}
              disabled={disabled}
              placeholder={placeholder || ze('请您将遇到的和产品相关的问题告诉我，用Shift+Enter换行。', 'Please tell me the product-related problems you encounter. Use Shift+Enter to wrap lines.')}
            />
          </div>
          <div className={hasLeftCluster ? scss.buttonContainerSpaced : scss.buttonContainer}>
            {hasLeftCluster ? (
              <div className={scss.leftCluster}>
                {leftElement || null}
              </div>
            ) : null}
            <div className={scss.rightCluster}>
              {onAttach && (
                <>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleBeforeUpload}
                    onChange={({ file }) => handleFileSelect(file)}
                  >
                    <Tooltip title={ze('上传截图', 'Screenshot')} placement="top">
                      <Button
                        className={scss.attachButton}
                        shape="circle"
                        icon={<CameraOutlined />}
                        disabled={isStreaming || disabled}
                      />
                    </Tooltip>
                  </Upload>
                  <Upload
                    showUploadList={false}
                    beforeUpload={handleBeforeUpload}
                    onChange={({ file }) => handleFileSelect(file)}
                  >
                    <Tooltip title={ze('上传文件', 'Attach file')} placement="top">
                      <Button
                        className={scss.attachButton}
                        shape="circle"
                        icon={<PaperClipOutlined />}
                        disabled={isStreaming || disabled}
                      />
                    </Tooltip>
                  </Upload>
                </>
              )}
              {sendOrStopBtn}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiChatBotInputBox;
